import * as React from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import Router, { useRouter } from 'next/router'
import withStyles from '@material-ui/core/styles/withStyles'
import { useUser } from 'api'
import { useTranslations } from 'containers/Translations/TranslationsContext'
import RouterLink from 'containers/RouterLink'
import ArrowPrev from 'components/icons/ArrowPrev'
import Button from 'components/Button'
import Container from 'components/Container'
import IconButton from 'components/IconButton'
import Link from 'components/Link'
import Section from 'components/Section'
import Typography from 'components/Typography'
import { useGlobal } from '../../api/GlobalContext/GlobalContext'

const BREAKPOINT_KEY_UP = 'sm'

export const styles = (theme) => ({
  root: {
    padding: theme.spacing(7, 0, 13, 0),
  },
  container: {
    display: 'grid',
    gridGap: theme.spacing(0.5),
    [theme.breakpoints.up(BREAKPOINT_KEY_UP)]: {
      gridGap: theme.spacing(6),
      gridTemplateColumns: '250px 1fr',
    },
  },
  aside: {},
  main: {
    paddingTop: `calc(${theme.spacing(4.5)}px + 46px)`,
    [theme.breakpoints.up(BREAKPOINT_KEY_UP)]: {
      paddingTop: theme.spacing(4.5),
    },
    overflow: 'hidden',
  },
  nav: {},
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2.8),
    margin: theme.spacing(8, 0, 6, 0),
  },
  listItem: {
    color: '#595463',
  },
  listItemText: {
    ...theme.typography.subtitle1,
    lineHeight: '22.5px',
  },
  backButton: {
    padding: theme.spacing(0, 1.5, 1.5, 1),
  },
  signOut: {
    padding: theme.spacing(1.5, 4),
  },
})

function UserSettingsLayout(props) {
  const { children, classes, className } = props

  const { user, onUserSignOut } = useUser()
  const router = useRouter()
  const t = useTranslations()
  const global = useGlobal()

  const [topLevel, setTopLevel] = React.useState(true)

  // TODO:: ADJUST THE LINKS, FILES , FOLDERS AND TRANSLATIONS
  const topLinks = React.useMemo(() => {
    return [
      !user.mlmMarket && {
        label: t('Web.Account.Menu.Dashboard'),
        url: '/account?dashboard=true',
        userTypeId: 2,
      },
      {
        label: t('Web.Account.Menu.MyOrdersLinkText'),
        url: '/account/orders-and-payments',
        userTypeId: 1,
      },
      {
        label: t('Web.Account.Menu.ManageMySubscriptionsLinkText'),
        url: '/account/manage-my-subscriptions',
        userTypeId: 1,
      },
      global.myBiomaShowOnWeb && {
        label: t('Web.Account.Menu.MyBioma'),
        url: '/account/my-gut-test',
        userTypeId: 1,
      },
      {
        label: t('Web.Account.Menu.MyDetailsLinkText'),
        url: '/account/settings',
        userTypeId: 1,
      },
      {
        label: t('Web.Account.Menu.MyPaymentMethodsLinkText'),
        url: '/account/payment-methods',
        userTypeId: 1,
      },
      {
        label: t('Web.Account.Menu.MarketingPreferencesLinkText'),
        url: '/account/marketing-preferences',
        userTypeId: 1,
      },
      {
        label: t('Web.Account.Menu.CreditsLinkText'),
        url: '/account/credits',
        userTypeId: 1,
      },
      {
        label: t('Web.Account.Menu.ReferAFriend.LinkText'),
        url: '/refer-a-friend',
        userTypeId: 1,
      },
      {
        label: t('Web.Account.Menu.Commissions.LinkText'),
        url: '/account/commissions',
        userTypeId: 2,
      },
    ]
  }, [global.myBiomaShowOnWeb, t, user.mlmMarket])

  const subLinks = React.useMemo(
    () => [
      {
        label: t('Web.Account.Menu.CommissionsTotal.LinkText'),
        url: '/account/commissions',
        userTypeId: 2,
      },
      {
        label: t('Web.Account.Menu.LifetimeCommissions.LinkText'),
        url: '/account/lifetime-commissions',
        userTypeId: 2,
      },
      {
        label: t('Web.Account.Menu.PrepaidBonuses.LinkText'),
        url: '/account/wallet',
        userTypeId: 2,
      },
      {
        label: t('Web.Account.Menu.BankDetails.LinkText'),
        url: '/account/bank-details',
        userTypeId: 2,
      },
      {
        label: t('Web.Account.Menu.PayoutHistory.LinkText'),
        url: '/account/payout-history',
        userTypeId: 2,
      },
    ],
    [t],
  )

  const handleSignOut = async () => {
    if (onUserSignOut) {
      onUserSignOut()
    }
  }

  const handleBackClick = React.useCallback(() => {
    router.push('/account')
  }, [router])

  React.useEffect(() => {
    if (subLinks.map((link) => link.url).some((link) => router.pathname.includes(link))) {
      setTopLevel(false)
    }
  }, [subLinks, router])

  React.useEffect(() => {
    const handleRouteChangeStart = () => {
      const main = document.getElementById('main')
      if (main && window.innerWidth < 600) {
        main.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }
    Router.events.on('routeChangeComplete', handleRouteChangeStart)

    // return () => {
    //   Router.events.off('routeChangeComplete', handleRouteChangeStart)
    // }
  }, [])

  return (
    <Section className={clsx(classes.root, className)}>
      <Container className={classes.container}>
        <div className={classes.aside}>
          <div className={classes.welcomeArea}>
            <IconButton
              onClick={handleBackClick}
              edge="start"
              title="Go back"
              className={classes.backButton}
            >
              <ArrowPrev />
            </IconButton>

            <Typography component="p" variant="h5">
              {topLevel
                ? t('Web.Dashboard.MyOrdersAccountSettings')
                : t('Web.Dashboard.MyComissions')}
            </Typography>
          </div>

          <nav className={classes.nav}>
            <ul className={classes.list}>
              {(topLevel ? topLinks : subLinks)
                .filter((link) => user.userTypeId >= link.userTypeId)
                .map((link, idx) => (
                  <li key={idx} className={classes.listItem}>
                    <Link
                      className={classes.ListItemText}
                      component={RouterLink}
                      href={link.url}
                      underline={link.url === router?.pathname ? 'always' : undefined}
                      variant="subtitle1"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
            </ul>
          </nav>

          <Button onClick={handleSignOut} variant="outlined" className={classes.signOut}>
            {t('Web.Account.Menu.SignOut')}
          </Button>
        </div>

        <div className={classes.main} id="main">
          {children}
        </div>
      </Container>
    </Section>
  )
}

UserSettingsLayout.propTypes = {
  children: PropTypes.node.isRequired,
  classes: PropTypes.object.isRequired,
  className: PropTypes.string,
}

export default withStyles(styles)(UserSettingsLayout)
